import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Camera, ArrowLeft, Users, Search, Phone, MessageCircle, Copy, Package, DollarSign, TrendingUp, Calendar, ExternalLink, LogOut } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useToast } from '../hooks/use-toast';
import { albumAPI } from '../lib/api';

export default function Clients() {
  const { currentUser, logoutUser } = useApp();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [data, setData] = useState({ total: 0, clients: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (!currentUser) { navigate('/auth'); return; }
    albumAPI.listClients().then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [currentUser, navigate]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.clients;
    return data.clients.filter(c => (c.name || '').toLowerCase().includes(q));
  }, [data, search]);

  const totalRevenue = data.clients.reduce((s, c) => s + (c.totalRevenue || 0), 0);
  const totalAlbums = data.clients.reduce((s, c) => s + (c.albumCount || 0), 0);

  if (!currentUser) return null;

  const openWhatsApp = (client) => {
    const phone = (client.contactPhone || '').replace(/\D/g, '');
    if (!phone) {
      toast({ title: 'Telefone não disponível', description: 'Este cliente ainda não deixou um telefone de contato.' });
      return;
    }
    const msg = `Olá ${client.contactName || client.name}! Aqui é do estúdio ${currentUser.name}.`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener');
  };

  return (
    <div className="grain min-h-screen">
      <header className="border-b border-[#1c1710]">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/painel" className="text-[#a89e87] hover:text-[#e6dcc7] inline-flex items-center gap-1.5 text-sm">
              <ArrowLeft size={16} /> Painel
            </Link>
            <div className="h-5 w-px bg-[#2a2318]" />
            <Link to="/" className="flex items-center gap-2.5">
              <span className="brand-mark"><Camera size={16} strokeWidth={2.2} /></span>
              <span className="font-serif text-[20px] text-[#f2ecdf]">Clientes</span>
            </Link>
          </div>
          <button onClick={async () => { await logoutUser(); navigate('/'); }} className="btn-outline-dark px-3 py-2 rounded-md text-sm inline-flex items-center gap-2">
            <LogOut size={15} /> Sair
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-8 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4 mb-8">
          <StatCard icon={Users} label="Clientes" value={data.total} />
          <StatCard icon={Package} label="Álbuns" value={totalAlbums} />
          <StatCard icon={DollarSign} label="Total faturado" value={`R$ ${totalRevenue.toFixed(2).replace('.', ',')}`} />
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7d735e]" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar cliente pelo nome…"
              data-testid="client-search"
              className="w-full bg-[#12100b] border border-[#2a2318] focus:border-[#d6af6c] focus:outline-none rounded-md pl-9 pr-3 py-2.5 text-sm text-[#f2ecdf]" />
          </div>
        </div>

        {loading && <div className="card-glow p-8 rounded-xl text-center text-[#a89e87]">Carregando…</div>}
        {!loading && filtered.length === 0 && (
          <div className="card-glow p-10 rounded-xl text-center">
            <Users size={36} className="mx-auto text-[#5c5343] mb-3" />
            <div className="text-[#a89e87]">{search ? 'Nenhum cliente encontrado com esse nome.' : 'Ainda não há clientes cadastrados. Eles aparecem aqui quando você cria um álbum.'}</div>
          </div>
        )}

        <div className="space-y-3">
          {filtered.map((c, i) => {
            const isOpen = expanded === i;
            return (
              <div key={i} data-testid={`client-row-${i}`} className="card-glow rounded-xl overflow-hidden">
                <div className="p-4 md:p-5 flex items-start gap-4 flex-wrap">
                  <div className="w-11 h-11 rounded-full bg-[#1a140c] border border-[#d6af6c]/30 flex items-center justify-center flex-shrink-0 gold-text font-serif text-lg">
                    {(c.name || 'C').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-[220px]">
                    <div className="text-[#f2ecdf] font-medium">{c.name}</div>
                    <div className="text-xs text-[#a89e87] mt-0.5 flex flex-wrap gap-x-3 gap-y-1">
                      <span className="inline-flex items-center gap-1"><Package size={11} /> {c.albumCount} álbu{c.albumCount === 1 ? 'm' : 'ns'}</span>
                      <span className="inline-flex items-center gap-1"><TrendingUp size={11} /> {c.totalSales} venda{c.totalSales !== 1 ? 's' : ''}</span>
                      <span className="inline-flex items-center gap-1 gold-text"><DollarSign size={11} /> R$ {Number(c.totalRevenue || 0).toFixed(2).replace('.', ',')}</span>
                      {c.lastActivity && <span className="inline-flex items-center gap-1"><Calendar size={11} /> {new Date(c.lastActivity).toLocaleDateString('pt-BR')}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setExpanded(isOpen ? null : i)} data-testid={`client-toggle-${i}`} className="btn-outline-dark px-3 py-2 rounded-md text-xs inline-flex items-center gap-1.5">
                      {isOpen ? 'Ocultar' : 'Ver álbuns'}
                    </button>
                    {c.contactPhone && (
                      <button onClick={() => openWhatsApp(c)} className="btn-outline-dark px-3 py-2 rounded-md text-xs text-[#25D366] inline-flex items-center gap-1.5">
                        <MessageCircle size={13} /> WhatsApp
                      </button>
                    )}
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t border-[#1c1710] bg-[#0e0c08]/40">
                    <div className="p-4 md:p-5 space-y-2">
                      {c.albums.map(a => (
                        <div key={a.code} className="flex items-center justify-between gap-3 p-3 rounded-md bg-[#12100b] border border-[#1c1710]">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-[#e6dcc7] truncate">{a.title || '(sem título)'}</div>
                            <div className="text-xs text-[#7d735e] mt-0.5">
                              {a.code} • {a.photoCount} foto{a.photoCount !== 1 ? 's' : ''} • {new Date(a.createdAt).toLocaleDateString('pt-BR')}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="gold-text text-sm">R$ {Number(a.revenue || 0).toFixed(2).replace('.', ',')}</div>
                            <div className="text-[10px] text-[#7d735e]">{a.sales} venda{a.sales !== 1 ? 's' : ''}</div>
                          </div>
                          <Link to={`/galeria/${a.code}`} className="btn-outline-dark px-2.5 py-1.5 rounded text-xs inline-flex items-center gap-1">
                            <ExternalLink size={11} /> Abrir
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="card-glow p-4 rounded-xl">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#a89e87] mb-1">
        <Icon size={12} /> {label}
      </div>
      <div className="font-serif text-2xl gold-text">{value}</div>
    </div>
  );
}
